import React, { useEffect, useRef, useState } from "react";
import { FaComments, FaLock, FaPaperPlane } from "react-icons/fa";
import { supabase } from "../supabaseClient";

function formatDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

export default function ApplicationChat({ applicationId }) {
  const [messages, setMessages] = useState([]);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const channelRef = useRef(null);

  const managementStarted = messages.some((item) => item.sender === "staff");

  useEffect(() => {
    if (!applicationId) return undefined;
    let active = true;

    async function load() {
      setLoading(true);
      const { data, error: loadError } = await supabase
        .from("dc_application_chat")
        .select("*")
        .eq("application_id", applicationId)
        .order("created_at", { ascending: true });
      if (!active) return;
      if (loadError) setError(loadError.message || "Messages could not be loaded.");
      else setMessages(data || []);
      setLoading(false);
    }

    void load();
    channelRef.current = supabase
      .channel(`application_chat_applicant_${applicationId}`)
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "dc_application_chat", filter: `application_id=eq.${applicationId}` }, (payload) => {
        setMessages((current) => current.some((item) => item.id === payload.new.id) ? current : [...current, payload.new]);
      })
      .subscribe();

    return () => {
      active = false;
      if (channelRef.current) supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    };
  }, [applicationId]);

  async function sendMessage() {
    const content = message.trim();
    if (!content || !managementStarted || sending) return;
    setSending(true);
    setError("");
    const { error: sendError } = await supabase.rpc("ruht_send_application_chat_message", {
      p_application_id: applicationId,
      p_message: content,
    });
    if (sendError) setError(sendError.message || "Your reply could not be sent.");
    else setMessage("");
    setSending(false);
  }

  return (
    <section className="pguh-applicant-chat">
      <div className="pguh-applicant-chat__heading">
        <div><FaComments aria-hidden="true" /><div><span className="pguh-eyebrow">Recruitment</span><h2>Messages from Management</h2></div></div>
        <strong>{messages.length}</strong>
      </div>

      {!managementStarted && !loading && (
        <div className="pguh-applicant-chat__locked">
          <FaLock aria-hidden="true" />
          <div><strong>Messaging has not been opened yet</strong><p>You can reply here once Management sends the first message about your application.</p></div>
        </div>
      )}

      <div className="pguh-applicant-chat__log" aria-live="polite">
        {loading ? <p>Loading messages…</p> : messages.length === 0 ? <p>No messages have been sent about this application yet.</p> : messages.map((item) => (
          <article key={item.id} className={`pguh-applicant-chat__message is-${item.sender}`}>
            <div><strong>{item.sender === "staff" ? "Management" : "You"}</strong><time>{formatDate(item.created_at)}</time></div>
            <p>{item.message}</p>
          </article>
        ))}
      </div>

      {error && <div className="nhsuk-error-message" role="alert">{error}</div>}

      <div className={`pguh-applicant-chat__compose${managementStarted ? "" : " is-locked"}`}>
        <textarea
          className="nhsuk-textarea"
          rows="4"
          maxLength="1500"
          value={message}
          disabled={!managementStarted || sending}
          onChange={(event) => setMessage(event.target.value)}
          placeholder={managementStarted ? "Write a reply to Management…" : "Management must message you first"}
        />
        <div><small>{message.length}/1500</small><button className="nhsuk-button" type="button" onClick={sendMessage} disabled={!managementStarted || !message.trim() || sending}><FaPaperPlane aria-hidden="true" /> {sending ? "Sending…" : "Send reply"}</button></div>
      </div>
    </section>
  );
}
